import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import StudentPicker from "../../components/StudentPicker";
import { generateCertification, listCertifications } from "../../api/certificationApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

/**
 * Direct port of digicampusbd's real, heavily-used
 * {@code student-report-for-certification-download} page (1,339 real
 * certifications issued). Unlike Testimonial/TransferCertificate, a
 * Certification has no independent edit form in the old app either — it's
 * generated fresh from the student's current data each time and logged.
 * The Jasper PDF itself is not ported (established precedent); "Generate"
 * renders the same narrative text inline for printing instead.
 */
export default function CertificationPage() {
  const [tab, setTab] = useState("generate");
  const [error, setError] = useState("");

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Certification</h1>
      </div>
      <Tabs
        tabs={[
          { key: "generate", label: "Generate" },
          { key: "list", label: "Issued Certifications" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>

      {tab === "generate" && <GenerateTab setError={setError} />}
      {tab === "list" && <ListTab setError={setError} />}
    </MainLayout>
  );
}

function GenerateTab({ setError }) {
  const [student, setStudent] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    setError("");
    if (!student) {
      setError("Select a student first.");
      return;
    }
    setGenerating(true);
    try {
      const data = await generateCertification(student.identificationId);
      setResult(data);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to generate certification.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <StudentPicker onSelect={setStudent} />
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleGenerate} disabled={generating || !student}>
          {generating ? "Generating..." : "Generate Certification"}
        </button>
      </div>

      {result && (
        <div style={{ marginTop: 16, border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8, padding: "1.5rem" }}>
          <h3>Certificate — {result.certificationSerial}</h3>
          <p>
            <strong>{result.studentName}</strong> ({result.customStudentId})
          </p>
          <p dangerouslySetInnerHTML={{ __html: result.certificationFirstPara }} />
          <p dangerouslySetInnerHTML={{ __html: result.certificationSecondPara }} />
          <p style={{ fontSize: "0.85rem", color: "#6b7280" }}>
            Issued by {result.issueBy} on {result.issueDate}
          </p>
        </div>
      )}
    </>
  );
}

function ListTab({ setError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listCertifications()
      .then(setRows)
      .catch(() => setError("Failed to load certifications."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <p>Loading...</p>;

  return (
    <DataTable
      columns={[
        { key: "certificationSerial", label: "Serial" },
        { key: "studentName", label: "Student" },
        { key: "customStudentId", label: "Student ID" },
        { key: "fatherName", label: "Father" },
        { key: "groupName", label: "Group" },
        { key: "issueDate", label: "Issue Date" },
        { key: "issueBy", label: "Issued By" },
      ]}
      rows={rows}
      emptyMessage="No certifications issued yet."
    />
  );
}
